<template>
  <q-page
    id="kitchen-orders-page"
    ref="kitchenOrdersPage"
    class="q-pa-sm overflow-hidden-y"
    style="scrollbar-width: none;"
    :style-fn="handlePageStyles"
  >
    <transition-group
      name="list-complete"
      class="column grid-masonry"
      :style="{ 'height': maxCardHeight }"
      tag="span"
      appear
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
    >
      <kitchen-card
        v-for="({ order, deliveryInstance, deliveryType, orderDueTime, orderItemsForInstance, orderItemsByStage }, index) in kitchenOrderItemsByOrderInstance"
        :key="`${order.id}_${deliveryInstance}_${orderDueTime}`"
        class="column list-complete-item q-mt-sm"
        :style="{
          'max-height': maxCardHeight,
          opacity: order.loading ? 0.5 : undefined,
          'pointer-events': order.loading ? 'none' : undefined
        }"
        :index="index"
        :kitchen-display="kitchenDisplay"
        :order="order"
        :delivery-type="deliveryType"
        :due-time="orderDueTime"
        :order-items="orderItemsForInstance"
        :order-items-by-stage="orderItemsByStage"
        :tab="tab"
        :zoom="zoom"
        @refresh="value => $emit('refresh', value)"
      />
    </transition-group>
    <q-btn-group
      v-if="kitchenDisplayType.showScrollControls"
      class="bg-white"
      style="width: 100%; height: 52px; position: fixed; bottom: 0; left: 0;"
      spread
      square
      flat
    >
      <q-btn
        icon="fas fa-chevrons-left"
        size="lg"
        @click="$refs.kitchenOrdersPage.$el.scroll({ top: 0, left: 0, behavior: 'smooth' })"
      />
      <q-separator vertical />
      <q-btn
        icon="fas fa-chevron-left"
        size="lg"
        @click="$refs.kitchenOrdersPage.$el.scrollBy({ top: 0, left: -840, behavior: 'smooth' })"
      />
      <q-separator vertical />
      <q-btn
        icon="fas fa-chevron-right"
        size="lg"
        @click="$refs.kitchenOrdersPage.$el.scrollBy({ top: 0, left: 840, behavior: 'smooth' })"
      />
      <q-separator vertical />
      <q-btn
        icon="fas fa-chevrons-right"
        size="lg"
        @click="$refs.kitchenOrdersPage.$el.scroll({ top: 0, left: $refs.kitchenOrdersPage.$el.scrollWidth, behavior: 'smooth' })"
      />
    </q-btn-group>
  </q-page>
</template>

<script>
import { GROUP_BY, STAGES } from 'src/models/kitchen-display/KitchenDisplayType'
import KitchenCard from 'src/modules/kds/components/kitchen-card/KitchenCard'

const KITCHEN_ORDERS_PAGE_Y_PADDING_PX = 16
const SCROLL_BUTTONS_Y_PX = 52
const REGISTER_Y_PADDING_PX = 87

export default {
  name: 'KitchenOrdersPage',
  configuration: ['currentSite'],
  components: {
    KitchenCard
  },
  inject: ['kitchenDisplayType'],
  props: {
    orders: {
      type: Object,
      required: true
    },
    kitchenDisplay: {
      type: Object,
      required: true
    },
    prepareBy: {
      type: String,
      required: false,
      default: GROUP_BY.NONE
    },
    deliveryWindowMinutes: {
      type: Number,
      required: false,
      default: 5
    },
    tab: {
      type: String,
      required: true
    },
    zoom: {
      type: Number,
      required: false,
      default: 1
    }
  },
  emits: ['refresh'],
  data () {
    return {
      maxCardHeight: '100%'
    }
  },
  computed: {
    kitchenOrderItemsByOrderInstance () {
      return _(this.orders.data)
        .map(order => {
          const orderItemsForOrder = _(order.orderItems.data).filter(this.isOrderItemVisible).value()
          // Group order items by delivery type, then map each delivery type group to an object with data for sort and display
          // We want to have a single array we can sort on, so we need to be able to flatten the nested delivery type / due time groups
          return _.chain(orderItemsForOrder)
            .groupBy(orderItem => `${orderItem.deliveryType}::${this.orderItemDueTime(orderItem)}`)
            .map((orderItemsForInstance, deliveryInstance) => {
              const firstOrderItem = _.first(orderItemsForInstance)
              return {
                order,
                deliveryInstance,
                deliveryType: firstOrderItem.deliveryType,
                orderDueTime: this.orderItemDueTime(firstOrderItem),
                rawDueTime: firstOrderItem.siteDueTime,
                // Group order items by:
                // 1. Stage (Current, Preparing, Delivering)
                // 2. Due Time
                // 3. Given PrepareBy identifier (e.g. item, variation, modifiers)
                orderItemsForInstance,
                orderItemsByStage: _.chain(orderItemsForInstance).groupBy(orderItem => {
                // If the order item state matches the current stage, return 'Current'
                // 'Current' groups will not be hidden under a collapsed expansion item
                  if (_.includes([STAGES.OPEN, STAGES.COMPLETE], this.tab)) return 'Current'

                  if (orderItem.isUnprepared) {
                    return this.tab === STAGES.PREPARE ? 'Current' : 'Preparing'
                  }

                  if (orderItem.isReadyForDelivery) {
                    return this.tab === STAGES.DELIVER ? 'Current' : 'Delivering'
                  }

                  return 'Current'
                }).mapValues(orderItemsForStage => {
                  return {
                    orderItemsForStage,
                    orderItemsByIdentifier: _(orderItemsForStage)
                      .sortBy(orderItem => !orderItem.item.requiresPreparation ? 1 : -orderItem.item.preparationTimeMinutes)
                      .groupBy(orderItem => {
                        let identifier = ''

                        // Ensure items in different delivery states are never grouped
                        if (orderItem.isUnprepared) {
                          identifier += 'Preparing_'
                        } else if (orderItem.isReadyForDelivery) {
                          identifier += 'Delivering_'
                        } else {
                          identifier += 'Delivered_'
                        }

                        if (this.prepareBy === GROUP_BY.MODIFIERS) {
                          identifier += orderItem.itemModifiersEquivalencyString
                        } else if (this.prepareBy === GROUP_BY.VARIATION) {
                          identifier += orderItem.itemOrderItem.itemVariation.id
                        } else if (this.prepareBy === GROUP_BY.ITEM) {
                          identifier += orderItem.itemOrderItem.item.id
                        } else {
                        // Fallback to GROUP_BY.NONE
                          identifier += orderItem.id
                        }

                        return identifier
                      }).value()
                  }
                }).value()
              }
            }).value()
        })
        .flatten()
        .sortBy('rawDueTime')
        .value()
    }
  },
  methods: {
    orderItemDueTime (orderItem) {
      if (!orderItem?.siteDueTime) return 'On Hold'

      return `Due ${this.$d(orderItem.siteDueTime, 'shortTime')}`
    },
    isOrderItemVisible (orderItem) {
      const shouldShowInCurrentTab = this.tab !== STAGES.COMPLETE ? orderItem.isUndelivered : !orderItem.isUndelivered

      return shouldShowInCurrentTab
    },
    handlePageStyles (offset) {
      if (this.$register) offset += REGISTER_Y_PADDING_PX

      let maxCardHeightOffset = offset + KITCHEN_ORDERS_PAGE_Y_PADDING_PX
      if (this.kitchenDisplayType.showScrollControls) maxCardHeightOffset += SCROLL_BUTTONS_Y_PX

      const zoomMulti = 1 / (this.zoom || 1)
      const pageHeight = offset ? `calc(${100 * zoomMulti}vh - ${offset * zoomMulti}px)` : '90vh'

      this.maxCardHeight = offset ? `calc(${100 * zoomMulti}vh - ${maxCardHeightOffset * zoomMulti}px)` : '90vh'

      return { minHeight: pageHeight, maxHeight: pageHeight }
    }
  }
}
</script>

<style scoped>
.grid-masonry {
  flex-direction: column;
  align-content: flex-start;
}
.grid-masonry:after {
    content: '';
    flex: 1 0 100% !important;
    width: 0 !important;
}

.list-complete-item {
  transition: all 0.5s, border 1ms, outline 1ms, padding 1ms, margin 1ms;
}
.list-complete-enter {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-active {
  position: absolute;
  width: 100%;
}
</style>
